import { useEffect, useLayoutEffect, useRef, useContext, useCallback, useImperativeHandle, forwardRef, useMemo } from 'react'
import { createChart, CandlestickSeries, createSeriesMarkers, type IChartApi, type ISeriesApi, type Time } from 'lightweight-charts'
import { ThemeContext } from '../../providers/ThemeProvider'
import { getCandleTimeRange, getCandleCountForTimeframe } from '../../utils/chartUtils'
import { getChartThemeOptions, binarySearchClosest } from '../../utils/chartHelpers'

interface TimeRange { start: number; end: number }

export interface CandleData {
  time: number
  open: number
  high: number
  low: number
  close: number
}

export interface OrderData {
  orderId: string | number
  time: number
  type: 'BUY' | 'SELL'
  price: number
  sl: number
  tp: number
  eventType: 'opened' | 'closed'
  volume?: number
  entryPrice?: number
  exitPrice?: number
  pnl?: number
  exitReason?: string
  balance?: number
  commissionCost?: number
}

export interface GraphCandleHandle {
  setVisibleLogicalRange: (range: { from: number; to: number }) => void
  getVisibleLogicalRange: () => { from: number; to: number } | null
  getCandleCount: () => number
}

interface GraphCandleProps {
  candles: CandleData[]
  orders?: OrderData[]
  symbol: string
  timeframe: string
  height?: number
  fixEdges?: boolean
  autoSetInitialVisibleRange?: boolean
  maxAvailableTimestamp?: number
  onVisibleRangeChange?: (range: { from: number; to: number }) => void
  onUserNavigate?: () => void
}

const GraphCandle = forwardRef<GraphCandleHandle, GraphCandleProps>(function GraphCandle(
  {
    candles,
    orders = [],
    symbol,
    timeframe,
    height,
    fixEdges = true,
    autoSetInitialVisibleRange = true,
    maxAvailableTimestamp,
    onVisibleRangeChange,
    onUserNavigate,
  },
  ref,
) {
  const { theme } = useContext(ThemeContext)
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null)
  const tooltipRef = useRef<HTMLDivElement | null>(null)
  const onVisibleRangeChangeRef = useRef(onVisibleRangeChange)
  onVisibleRangeChangeRef.current = onVisibleRangeChange
  const onUserNavigateRef = useRef(onUserNavigate)
  onUserNavigateRef.current = onUserNavigate
  const themeRef = useRef(theme)
  themeRef.current = theme
  // Symbol/timeframe live in refs so the chart-creation effect doesn't need
  // them as deps. Watermark text + per-frame timeframe lookups always read
  // the latest value. Without this, every timeframe switch would tear down
  // and recreate the entire chart.
  const symbolRef = useRef(symbol)
  symbolRef.current = symbol
  const timeframeRef = useRef(timeframe)
  timeframeRef.current = timeframe
  const dragStartXRef = useRef<number | null>(null)
  const dragNavigatedRef = useRef(false)

  const chartVersionRef = useRef(0)
  const initialRangeSetRef = useRef(0)
  const markersApiRef = useRef<ReturnType<typeof createSeriesMarkers<Time>> | null>(null)

  const allOrdersRef = useRef<(OrderData & { normalizedTimestamp: number })[]>([])
  // Tracks the last-set series data so the data layoutEffect can detect a
  // strict suffix-append and use series.update() instead of a full setData
  // (which re-uploads the entire array on every replay tick).
  const lastSeriesSnapshotRef = useRef<{
    firstTime: number
    lastTime: number
    count: number
  } | null>(null)

  const displayCandles = useMemo(() => {
    if (maxAvailableTimestamp == null || candles.length === 0) return candles
    let lo = 0
    let hi = candles.length
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (candles[mid].time <= maxAvailableTimestamp) lo = mid + 1
      else hi = mid
    }
    return lo === candles.length ? candles : candles.slice(0, lo)
  }, [candles, maxAvailableTimestamp])

  useImperativeHandle(ref, () => ({
    setVisibleLogicalRange(range: { from: number; to: number }) {
      chartRef.current?.timeScale().setVisibleLogicalRange(range)
    },
    getVisibleLogicalRange() {
      const r = chartRef.current?.timeScale().getVisibleLogicalRange()
      return r ? { from: r.from, to: r.to } : null
    },
    getCandleCount() {
      return displayCandles.length
    },
  }))

  // Reads timeframeRef so this stays referentially stable across timeframe
  // changes — otherwise it would force the chart-creation effect to re-run.
  const findOrdersForCandle = useCallback((timestamp: number): OrderData[] => {
    const timeRange = getCandleTimeRange(timestamp, timeframeRef.current) as TimeRange
    return allOrdersRef.current.filter(
      o => o.normalizedTimestamp >= timeRange.start && o.normalizedTimestamp <= timeRange.end,
    )
  }, [])

  const hideTooltip = useCallback(() => {
    if (tooltipRef.current) tooltipRef.current.style.display = 'none'
  }, [])

  const handlePointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    dragStartXRef.current = event.clientX
    dragNavigatedRef.current = false
  }, [])

  const handlePointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (dragStartXRef.current === null || dragNavigatedRef.current) return
    if (Math.abs(event.clientX - dragStartXRef.current) < 8) return
    dragNavigatedRef.current = true
    hideTooltip()
    onUserNavigateRef.current?.()
  }, [hideTooltip])

  const clearPointerDrag = useCallback(() => {
    dragStartXRef.current = null
    dragNavigatedRef.current = false
  }, [])

  const handleTouchStart = useCallback(() => {
    hideTooltip()
    onUserNavigateRef.current?.()
  }, [hideTooltip])

  const handleWheel = useCallback((event: React.WheelEvent<HTMLDivElement>) => {
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) && !event.shiftKey) return
    hideTooltip()
    onUserNavigateRef.current?.()
  }, [hideTooltip])

  useEffect(() => {
    const normalized = orders.map(o => ({
      ...o,
      normalizedTimestamp: typeof o.time === 'number' ? o.time : Math.floor(new Date(String(o.time).endsWith('Z') ? String(o.time) : String(o.time) + 'Z').getTime() / 1000),
    }))
    normalized.sort((a, b) => a.normalizedTimestamp - b.normalizedTimestamp)
    allOrdersRef.current = normalized
  }, [orders])

  // ---- Chart creation (useLayoutEffect so seriesRef is set before data-setting layoutEffect) ----
  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return

    if (chartRef.current) {
      chartRef.current.remove()
      chartRef.current = null
      seriesRef.current = null
      markersApiRef.current = null
    }

    el.style.position = 'relative'

    if (!tooltipRef.current) {
      const tip = document.createElement('div')
      tip.style.cssText = `
        display:none;position:absolute;padding:8px 10px;border-radius:6px;
        box-shadow:0 2px 8px rgba(0,0,0,0.15);z-index:10;font-size:12px;
        max-width:280px;pointer-events:none;line-height:1.5;
      `
      el.appendChild(tip)
      tooltipRef.current = tip
    } else {
      tooltipRef.current.style.display = 'none'
    }

    const curTheme = themeRef.current
    const isDark = curTheme === 'dark'
    const tipEl = tooltipRef.current!
    tipEl.style.backgroundColor = isDark ? '#2A2A2A' : 'rgba(255,255,255,0.95)'
    tipEl.style.color = isDark ? '#F1F5F9' : '#212b36'
    tipEl.style.border = `1px solid ${isDark ? '#383838' : '#ddd'}`

    const themeOpts = getChartThemeOptions(curTheme, { fixLeftEdge: fixEdges, fixRightEdge: fixEdges })

    const effectiveHeight = height ?? el.clientHeight
    const chart = createChart(el, {
      height: effectiveHeight,
      ...themeOpts,
      watermark: {
        visible: true,
        text: `${symbolRef.current} - ${timeframeRef.current}`,
        color: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
        fontSize: 40,
      },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any)

    const series = chart.addSeries(CandlestickSeries, {
      upColor: '#26a69a',
      downColor: '#ef5350',
      borderVisible: false,
      wickUpColor: '#26a69a',
      wickDownColor: '#ef5350',
      priceFormat: {
        type: 'price',
        precision: 5,
        minMove: 0.00001,
      },
    })

    chartRef.current = chart
    seriesRef.current = series
    chartVersionRef.current++
    // Reset the strict-append cache; a fresh chart starts with no series data.
    lastSeriesSnapshotRef.current = null

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    markersApiRef.current = createSeriesMarkers(series, [] as any)

    chart.subscribeClick((param) => {
      if (!param || !param.time || !param.point || param.point.x < 0 || param.point.y < 0) {
        hideTooltip()
        return
      }

      const matched = findOrdersForCandle(param.time as number)
      if (matched.length === 0) { hideTooltip(); return }

      const tip = tooltipRef.current
      if (!tip) return

      const dark = themeRef.current === 'dark'
      const opened = matched.filter(o => o.eventType === 'opened')
      const closed = matched.filter(o => o.eventType === 'closed')
      const border = dark ? '#383838' : '#ddd'

      const mkDiv = (css?: string) => { const d = document.createElement('div'); if (css) d.style.cssText = css; return d }
      const mkSpan = (text: string, css?: string) => { const s = document.createElement('span'); s.textContent = text; if (css) s.style.cssText = css; return s }

      tip.textContent = ''

      const header = mkDiv(`font-weight:600;margin-bottom:4px;padding-bottom:4px;border-bottom:1px solid ${border}`)
      header.textContent = `Orders (${matched.length})`
      tip.appendChild(header)

      if (opened.length > 0) {
        const label = mkDiv('font-size:11px;font-weight:600;opacity:0.6;margin-bottom:2px')
        label.textContent = 'Opened'
        tip.appendChild(label)
        for (const o of opened) {
          const arrow = o.type === 'BUY' ? '\u25B2' : '\u25BC'
          const row = mkDiv('margin-bottom:4px')
          row.appendChild(mkSpan(`${arrow} ${o.type}`, 'color:#9E9E9E;font-weight:600'))
          if (o.volume != null) row.appendChild(mkSpan(`${o.volume.toFixed(2)} lot`, 'opacity:0.5;margin-left:4px;font-size:11px'))
          const details = mkDiv('display:flex;gap:10px;font-size:11px;opacity:0.85;margin-top:1px')
          details.appendChild(mkSpan(`Entry: ${o.price.toFixed(5)}`))
          details.appendChild(mkSpan(`SL: ${o.sl.toFixed(5)}`))
          details.appendChild(mkSpan(`TP: ${o.tp.toFixed(5)}`))
          row.appendChild(details)
          tip.appendChild(row)
        }
      }

      if (closed.length > 0) {
        if (opened.length > 0) tip.appendChild(mkDiv(`border-top:1px solid ${border};margin:4px 0`))
        const label = mkDiv('font-size:11px;font-weight:600;opacity:0.6;margin-bottom:2px')
        label.textContent = 'Closed'
        tip.appendChild(label)
        for (const o of closed) {
          const arrow = o.type === 'BUY' ? '\u25B2' : '\u25BC'
          const pnl = o.pnl ?? 0
          const pnlColor = pnl > 0 ? '#00C853' : '#FF1744'
          const pnlSign = pnl > 0 ? '+' : ''
          const row = mkDiv('margin-bottom:4px')
          row.appendChild(mkSpan(`${arrow} ${o.type}`, `color:${pnlColor};font-weight:600`))
          row.appendChild(mkSpan(`${pnlSign}${pnl.toFixed(2)}`, `color:${pnlColor};font-weight:600;margin-left:6px`))
          if (o.volume != null) row.appendChild(mkSpan(`${o.volume.toFixed(2)} lot`, 'opacity:0.5;margin-left:4px;font-size:11px'))
          const details = mkDiv('display:flex;gap:10px;font-size:11px;opacity:0.85;margin-top:1px')
          details.appendChild(mkSpan(`${(o.entryPrice ?? o.price).toFixed(5)} \u2192 ${(o.exitPrice ?? o.price).toFixed(5)}`))
          if (o.exitReason) details.appendChild(mkSpan(o.exitReason))
          row.appendChild(details)
          tip.appendChild(row)
        }
      }

      tip.style.display = 'block'

      const rect = el.getBoundingClientRect()
      const tw = tip.offsetWidth
      let left = param.point.x + 16
      if (left + tw > rect.width) left = param.point.x - tw - 10
      left = Math.max(0, left)
      tip.style.left = `${left}px`
      tip.style.top = `${Math.max(0, param.point.y - 20)}px`
    })

    chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
      hideTooltip()
      if (range && onVisibleRangeChangeRef.current) {
        onVisibleRangeChangeRef.current({ from: range.from, to: range.to })
      }
    })

    const ro = new ResizeObserver(() => {
      if (el && chartRef.current) {
        chartRef.current.resize(el.clientWidth, height ?? el.clientHeight)
      }
    })
    ro.observe(el)

    return () => {
      ro.disconnect()
      if (chartRef.current) {
        chartRef.current.remove()
        chartRef.current = null
        seriesRef.current = null
        markersApiRef.current = null
      }
      if (tooltipRef.current && tooltipRef.current.parentNode) {
        tooltipRef.current.parentNode.removeChild(tooltipRef.current)
        tooltipRef.current = null
      }
    }
    // Symbol/timeframe intentionally omitted: they're surfaced via refs and
    // applied through applyOptions in the theme effect below, avoiding a full
    // chart teardown every time the user switches timeframe.
  }, [height, fixEdges, findOrdersForCandle, hideTooltip])

  // ---- Theme hot-swap (no chart recreation) ----
  useEffect(() => {
    if (!chartRef.current) return
    const opts = getChartThemeOptions(theme, { fixLeftEdge: fixEdges, fixRightEdge: fixEdges })
    const isDark = theme === 'dark'
    chartRef.current.applyOptions({
      ...opts,
      // @ts-expect-error lightweight-charts typings omit watermark in this build.
      watermark: {
        visible: true,
        text: `${symbol} - ${timeframe}`,
        color: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
        fontSize: 40,
      },
    })
    if (tooltipRef.current) {
      tooltipRef.current.style.backgroundColor = isDark ? '#2A2A2A' : 'rgba(255,255,255,0.95)'
      tooltipRef.current.style.color = isDark ? '#F1F5F9' : '#212b36'
      tooltipRef.current.style.border = `1px solid ${isDark ? '#383838' : '#ddd'}`
    }
  }, [theme, fixEdges, symbol, timeframe])

  // ---- Data update (useLayoutEffect to sync with parent range adjustments before paint) ----
  // Detect strict suffix-append (the common case during replay) and use
  // series.update() per new candle instead of re-uploading the entire array
  // via setData. Falls back to setData when the data shifted (loadWindow,
  // pruning, or the first paint).
  useLayoutEffect(() => {
    if (!seriesRef.current) return

    if (displayCandles.length === 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      seriesRef.current.setData([] as any)
      lastSeriesSnapshotRef.current = null
      return
    }

    const snapshot = lastSeriesSnapshotRef.current
    const firstTime = displayCandles[0].time
    const lastTime = displayCandles[displayCandles.length - 1].time

    const canStrictAppend =
      snapshot !== null &&
      snapshot.firstTime === firstTime &&
      displayCandles.length > snapshot.count &&
      displayCandles[snapshot.count].time > snapshot.lastTime

    if (canStrictAppend) {
      for (let i = snapshot!.count; i < displayCandles.length; i++) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        seriesRef.current.update(displayCandles[i] as any)
      }
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      seriesRef.current.setData(displayCandles as any)
    }

    lastSeriesSnapshotRef.current = {
      firstTime,
      lastTime,
      count: displayCandles.length,
    }

    if (
      autoSetInitialVisibleRange &&
      initialRangeSetRef.current !== chartVersionRef.current
    ) {
      initialRangeSetRef.current = chartVersionRef.current
      const visibleCount = getCandleCountForTimeframe(timeframe, displayCandles.length)
      const to = displayCandles.length - 1
      const from = Math.max(0, to - visibleCount + 1)
      chartRef.current?.timeScale().setVisibleLogicalRange({ from, to })
    }
  }, [displayCandles, timeframe, autoSetInitialVisibleRange])

  // ---- Markers update ----
  useEffect(() => {
    if (!markersApiRef.current) return
    if (displayCandles.length === 0) {
      try { markersApiRef.current.setMarkers([]) } catch { /* noop */ }
      return
    }

    if (allOrdersRef.current.length === 0) {
      try { markersApiRef.current.setMarkers([]) } catch { /* noop */ }
      return
    }

    const ordersByCandle: Record<string, { candle: number; type: string; orders: OrderData[] }> = {}

    const firstDisplayedTs = displayCandles[0].time
    const lastDisplayedTs = displayCandles[displayCandles.length - 1].time

    // Returns null when the order falls outside the displayed candle window.
    // Without this check, orders earlier than firstDisplayedTs were collapsed
    // onto displayCandles[0], producing a clump of misplaced markers after
    // the candle array gets pruned by extendWindow.
    const findCandle = (ts: number): number | null => {
      const range = getCandleTimeRange(ts, timeframe) as TimeRange
      if (range.end < firstDisplayedTs || range.start > lastDisplayedTs) return null

      const idx = binarySearchClosest(displayCandles, ts)
      if (displayCandles[idx].time === ts) return ts

      let lo = 0
      let hi = displayCandles.length - 1
      while (lo < hi) {
        const mid = (lo + hi) >> 1
        if (displayCandles[mid].time < range.start) lo = mid + 1
        else hi = mid
      }
      for (let i = lo; i < displayCandles.length && displayCandles[i].time <= range.end; i++) {
        return displayCandles[i].time
      }

      return null
    }

    for (const order of allOrdersRef.current) {
      const candleTime = findCandle(order.normalizedTimestamp)
      if (candleTime === null) continue
      const key = `${candleTime}:${order.type.toLowerCase()}`
      if (!ordersByCandle[key]) {
        ordersByCandle[key] = { candle: candleTime, type: order.type.toLowerCase(), orders: [] }
      }
      ordersByCandle[key].orders.push(order)
    }

    const markers = Object.values(ordersByCandle).map(g => {
      const isBuy = g.type === 'buy'
      const closedOrders = g.orders.filter(o => o.eventType === 'closed')
      let color: string
      if (closedOrders.length === 0) {
        color = '#9E9E9E'
      } else if (closedOrders.length === 1 && g.orders.length === 1) {
        color = (closedOrders[0].pnl ?? 0) > 0 ? '#00C853' : '#FF1744'
      } else {
        color = '#9E9E9E'
      }
      return {
        time: g.candle,
        position: isBuy ? 'belowBar' as const : 'aboveBar' as const,
        color,
        shape: isBuy ? 'arrowUp' as const : 'arrowDown' as const,
        text: g.orders.length > 1 ? `${g.orders.length}` : '',
        size: 1,
      }
    })

    markers.sort((a, b) => (a.time as number) - (b.time as number))

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      markersApiRef.current.setMarkers(markers as any)
    } catch {
      /* noop – fallback if API differs */
    }
  }, [displayCandles, orders, timeframe])

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: height != null ? height : '100%' }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={clearPointerDrag}
      onPointerCancel={clearPointerDrag}
      onPointerLeave={clearPointerDrag}
      onTouchStart={handleTouchStart}
      onWheel={handleWheel}
    />
  )
})

export default GraphCandle
